import * as XLSX from 'xlsx';

// "GST Upload Tracker" tab for the EMS dashboard. Injected into the EMS shadow
// root after emsSource has rendered (see EmsDashboard.jsx), so the legacy
// markup/script stays untouched. Reuses EMS's existing classes (.ni, .sec, .fb,
// .fp, .kpi-strip, .tbl-wrap, .pill) plus a few scoped styles below.

const SEC_ID = 's10';
const SUM_ID = 's11';

const STATUS_ORDER = [
  'Due - upload now',
  'Password issue',
  'Tried - error',
  'Tried - no data found',
  'Locked (24h rule)',
  'No credentials',
  'Uploaded today',
];
const STATUS_CLASS = {
  'Uploaded today': 'gst-ok',
  'Locked (24h rule)': 'gst-lock',
  'Due - upload now': 'gst-warn',
  'Password issue': 'gst-bad',
  'Tried - error': 'gst-bad',
  'Tried - no data found': 'gst-idle',
  'No credentials': 'gst-idle2',
};
const ISSUE_LABELS = {
  PASSWORD_INCORRECT: 'Password incorrect',
  PASSWORD_EXPIRED: 'Password expired',
  NO_DATA_FOUND: 'No data found',
  UPLOAD: 'Upload',
};
const UNASSIGNED = 'Unassigned';
const MANUAL = 'Manual Upload';

export const GST_TAB_NAV = `<div class="ni" id="gst-nav-tracker" style="border-left:2px solid #E5E7EB;margin-left:6px;padding-left:14px;" onclick="showSec('${SEC_ID}',this)">GST Upload Tracker</div>
<div class="ni" id="gst-nav-summary" onclick="showSec('${SUM_ID}',this)">GST Summary</div>`;

const GST_TAB_CSS = `
/* Colours come from the EMS theme variables (--surface-*, --border-*, --text-*), so light and dark both work */
#${SEC_ID} .gst-spill{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;white-space:nowrap;}
#${SEC_ID} .gst-ok{background:#DCFCE7;color:#166534;}
#${SEC_ID} .gst-lock{background:#E0E7FF;color:#3730A3;}
#${SEC_ID} .gst-warn{background:#FEF3C7;color:#92400E;}
#${SEC_ID} .gst-bad{background:#FEE2E2;color:#991B1B;}
#${SEC_ID} .gst-idle{background:#F3F4F6;color:#374151;}
#${SEC_ID} .gst-idle2{background:#E5E7EB;color:#4B5563;}
#${SEC_ID} .gst-search,#${SEC_ID} .gst-sel{background:var(--surface-2,#fff);color:var(--text-primary,#18181b);border:1px solid var(--border-strong,#d4d4d8);border-radius:6px;}
#${SEC_ID} .gst-search{padding:4px 10px;font-size:12px;min-width:200px;}
#${SEC_ID} .gst-sel{padding:3px 6px;font-size:11px;max-width:190px;}
#${SEC_ID} .gst-sel option{background:var(--surface-2,#fff);color:var(--text-primary,#18181b);}
#${SEC_ID} .gst-search::placeholder{color:var(--text-muted,#71717a);}
#${SEC_ID} .gst-btn{padding:4px 12px;border:1px solid var(--fill-accent,#4f46e5);border-radius:6px;background:var(--fill-accent,#4f46e5);color:var(--on-accent,#fff);font-size:11px;cursor:pointer;}
#${SEC_ID} .gst-btn.ghost{background:var(--surface-2,#fff);color:var(--text-secondary,#52525b);border-color:var(--border-strong,#d4d4d8);}
#${SEC_ID} .gst-total-row td{background:var(--surface-3,#f4f4f5) !important;}
:host([data-theme="dark"]) #${SEC_ID} .gst-ok{background:#052e16;color:#86efac;}
:host([data-theme="dark"]) #${SEC_ID} .gst-lock{background:#1e1b4b;color:#c7d2fe;}
:host([data-theme="dark"]) #${SEC_ID} .gst-warn{background:#422006;color:#fcd34d;}
:host([data-theme="dark"]) #${SEC_ID} .gst-bad{background:#450a0a;color:#fca5a5;}
:host([data-theme="dark"]) #${SEC_ID} .gst-idle{background:#27272a;color:#d4d4d8;}
:host([data-theme="dark"]) #${SEC_ID} .gst-idle2{background:#3f3f46;color:#e4e4e7;}
:host([data-theme="dark"]) #${SEC_ID} .kpi-big.g{color:#86efac;}
:host([data-theme="dark"]) #${SEC_ID} .kpi-big.a{color:#fcd34d;}
:host([data-theme="dark"]) #${SEC_ID} .kpi-big.r{color:#fca5a5;}
:host([data-theme="dark"]) #${SEC_ID} .kpi-lbl{color:#a1a1aa;}
#${SEC_ID} th.gst-sort{cursor:pointer;user-select:none;}
#${SEC_ID} .gst-mono{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;}
#${SEC_ID} .gst-asof{margin-left:auto;font-size:11px;color:var(--text-muted,#71717a);align-self:center;}
/* Count strip: equal-width boxes; labels reserve two lines so every number sits on the same baseline */
#${SEC_ID} .kpi-strip{display:grid;grid-template-columns:repeat(auto-fit,minmax(112px,1fr));}
#${SEC_ID} .kpi{display:flex;flex-direction:column;justify-content:space-between;gap:6px;padding:12px 14px;min-width:0;border-bottom:1px solid var(--border,#e4e4e7);}
#${SEC_ID} .kpi-lbl{min-height:2.6em;line-height:1.3;margin-bottom:0;}
#${SEC_ID} .kpi-big{font-size:26px;}
`;

const COLUMNS = [
  ['owner_label', 'Responsible Person'],
  ['client_name', 'Client'],
  ['activation_status', 'Client Status'],
  ['gst_number', 'GST Number'],
  ['state', 'State'],
  ['upload_status', 'Upload Status Today'],
  ['last_upload_at', 'Last Upload (IST)'],
  ['next_upload_allowed_at', 'Next Upload Allowed (IST)'],
  ['seconds_until_allowed', 'Time Until Allowed'],
  ['days_since_last_upload', 'Days Since Last Upload'],
  ['uploads_today', 'Uploads Today'],
  ['issue_today', "Today's Issue"],
  ['last_action_at', 'Last Activity (IST)'],
  ['first_upload_at', 'First Upload (IST)'],
  ['pan_number', 'PAN'],
  ['gst_created_at', 'GST Added On (IST)'],
];

const esc = v =>
  String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// pg returns timestamp-without-tz as UTC; make sure it parses as UTC.
const toDate = v => {
  if (!v) return null;
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const IST_FMT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
export const fmtIst = v => {
  const d = toDate(v);
  return d ? IST_FMT.format(d).replace(',', ',') : '';
};
export const fmtRemaining = secs => {
  if (!secs || secs <= 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const issueLabel = v => (v === null || v === undefined ? '' : ISSUE_LABELS[v] || 'Error in downloading');

export const donePct = rows => {
  const done = rows.filter(r => r.upload_status === 'Uploaded today').length;
  const denom = rows.filter(r => r.upload_status !== 'Locked (24h rule)' && r.upload_status !== 'No credentials').length;
  return denom ? Math.round((done / denom) * 100) : null;
};
const actionNeeded = rows =>
  rows.filter(r => ['Due - upload now', 'Password issue', 'Tried - error'].includes(r.upload_status)).length;

export const summarizeByOwner = rows => {
  const groups = new Map();
  rows.forEach(r => {
    if (!groups.has(r.owner_label)) groups.set(r.owner_label, []);
    groups.get(r.owner_label).push(r);
  });
  const line = (owner, rs) => {
    const n = st => rs.filter(r => r.upload_status === st).length;
    return {
      owner, total: rs.length, uploaded: n('Uploaded today'), locked: n('Locked (24h rule)'), due: n('Due - upload now'),
      password: n('Password issue'), noData: n('Tried - no data found'), error: n('Tried - error'),
      noCreds: n('No credentials'), donePct: donePct(rs), action: actionNeeded(rs),
    };
  };
  const out = [...groups.entries()]
    .sort((a, b) => ownerRank(a[0]) - ownerRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([o, rs]) => line(o, rs));
  return { owners: out, total: line('Total', rows) };
};

const rowDisplayValue = (r, col) => {
  switch (col) {
    case 'client_name': return `${r.client_name} [${r.client_id}]`;
    case 'last_upload_at': case 'next_upload_allowed_at': case 'last_action_at':
    case 'first_upload_at': case 'gst_created_at':
      return col === 'next_upload_allowed_at' && !r.last_upload_at ? 'Any time' : fmtIst(r[col]);
    case 'seconds_until_allowed': return fmtRemaining(r.seconds_until_allowed);
    case 'issue_today': return issueLabel(r.issue_today);
    default: return r[col] ?? '';
  }
};

const ownerRank = o => (o === UNASSIGNED ? 2 : o === MANUAL ? 1 : 0);
const defaultCompare = (a, b) =>
  ownerRank(a.owner_label) - ownerRank(b.owner_label) ||
  String(a.owner_label).localeCompare(String(b.owner_label)) ||
  STATUS_ORDER.indexOf(a.upload_status) - STATUS_ORDER.indexOf(b.upload_status) ||
  String(a.client_name).localeCompare(String(b.client_name)) ||
  String(a.gst_number).localeCompare(String(b.gst_number));

export function mountGstUploadTab(root, { baseUrl, requestOptions, bearerToken, canAssign }) {
  const nav = root.querySelector('.nav');
  const footer = root.querySelector('.footer');
  if (!nav || !footer || root.getElementById(SEC_ID)) return;

  nav.insertAdjacentHTML('beforeend', GST_TAB_NAV);
  const style = document.createElement('style');
  style.textContent = GST_TAB_CSS;
  root.appendChild(style);
  footer.insertAdjacentHTML('beforebegin', `
<div id="${SEC_ID}" class="sec">
  <div class="fb filterbar">
    <div class="filter-group" id="gst-status-pills"><span class="flbl">Status:</span></div>
    <div class="filter-group"><span class="flbl">Owner:</span><select class="gst-sel" id="gst-owner"></select></div>
    <div class="filter-group"><span class="flbl">Client status:</span>
      <select class="gst-sel" id="gst-act"><option value="">All</option><option>ACTIVE</option><option>TRIAL</option></select></div>
    <input class="gst-search" id="gst-q" type="search" placeholder="Search client / GST / PAN…" />
    <button class="gst-btn ghost" id="gst-refresh" type="button">Refresh</button>
    <button class="gst-btn" id="gst-export" type="button">Export to Excel</button>
    <span class="gst-asof" id="gst-asof"></span>
  </div>
  <div class="kpi-strip" id="gst-kpis"></div>
  <div class="tbl-wrap"><table>
    <thead><tr id="gst-head"></tr></thead>
    <tbody id="gst-body"><tr><td colspan="${COLUMNS.length}" class="no-rows">Loading…</td></tr></tbody>
  </table></div>
</div>
<div id="${SUM_ID}" class="sec">
  <div class="fb filterbar">
    <span class="flbl">Summary by responsible person — click a row to open its GST numbers</span>
    <button class="gst-btn ghost" id="gst-sum-refresh" type="button">Refresh</button>
    <button class="gst-btn" id="gst-sum-export" type="button">Export to Excel</button>
    <span class="gst-asof" id="gst-sum-asof"></span>
  </div>
  <div class="tbl-wrap"><table>
    <thead><tr>
      <th>Responsible Person</th><th>Total</th><th>Uploaded today</th><th>Locked (24h)</th><th>Due</th>
      <th>Password issue</th><th>No data</th><th>Error</th><th>No credentials</th><th>Done %</th><th>Action needed</th>
    </tr></thead>
    <tbody id="gst-sum-body"><tr><td colspan="11" class="no-rows">Loading…</td></tr></tbody>
  </table></div>
</div>`);

  const $ = id => root.getElementById(id);
  const state = { rows: [], users: [], statuses: new Set(), owner: '', act: '', q: '', sort: null, asOf: null };

  async function load() {
    $('gst-body').innerHTML = `<tr><td colspan="${COLUMNS.length}" class="no-rows">Loading…</td></tr>`;
    $('gst-sum-body').innerHTML = '<tr><td colspan="11" class="no-rows">Loading…</td></tr>';
    try {
      const res = await fetch(`${baseUrl}/GstUploadStatus`, requestOptions);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      state.rows = data.gsts || [];
      state.asOf = data.generatedAt;
      if (canAssign && !state.users.length) {
        const ur = await fetch(`${baseUrl}/GstUploadOwnerChoices`, requestOptions);
        if (ur.ok) state.users = (await ur.json()).users || [];
      }
      renderAll();
    } catch (e) {
      $('gst-body').innerHTML = `<tr><td colspan="${COLUMNS.length}" class="no-rows">Failed to load: ${esc(e.message)}</td></tr>`;
      $('gst-sum-body').innerHTML = `<tr><td colspan="11" class="no-rows">Failed to load: ${esc(e.message)}</td></tr>`;
    }
  }

  function filtered() {
    const q = state.q.trim().toLowerCase();
    let rows = state.rows.filter(r =>
      (!state.statuses.size || state.statuses.has(r.upload_status)) &&
      (!state.owner || r.owner_label === state.owner) &&
      (!state.act || r.activation_status === state.act) &&
      (!q || `${r.client_name} ${r.client_id} ${r.gst_number} ${r.pan_number}`.toLowerCase().includes(q)));
    if (state.sort) {
      const { col, asc } = state.sort;
      rows = [...rows].sort((a, b) => {
        const av = a[col], bv = b[col];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const c = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return asc ? c : -c;
      });
    } else rows = [...rows].sort(defaultCompare);
    return rows;
  }

  function renderControls() {
    const counts = {};
    state.rows.forEach(r => { counts[r.upload_status] = (counts[r.upload_status] || 0) + 1; });
    $('gst-status-pills').innerHTML = '<span class="flbl">Status:</span>' + STATUS_ORDER
      .filter(s => counts[s])
      .map(s => `<span class="fp${state.statuses.has(s) ? ' on' : ''}" data-status="${esc(s)}">${esc(s)} (${counts[s]})</span>`)
      .join('');
    const owners = [...new Set(state.rows.map(r => r.owner_label))].sort((a, b) => ownerRank(a) - ownerRank(b) || a.localeCompare(b));
    $('gst-owner').innerHTML = '<option value="">All</option>' + owners.map(o => `<option${o === state.owner ? ' selected' : ''}>${esc(o)}</option>`).join('');
    $('gst-asof').textContent = state.asOf ? `Data as of ${fmtIst(state.asOf)} IST` : '';
  }

  function renderKpis(rows) {
    const n = s => rows.filter(r => r.upload_status === s).length;
    const pct = donePct(rows);
    const k = (lbl, val, cls = '') => `<div class="kpi"><div class="kpi-lbl">${lbl}</div><div class="kpi-big ${cls}">${val}</div></div>`;
    $('gst-kpis').innerHTML =
      k('GST numbers', rows.length) + k('Uploaded today', n('Uploaded today'), 'g') + k('Locked (24h)', n('Locked (24h rule)')) +
      k('Due', n('Due - upload now'), 'a') + k('Password issue', n('Password issue'), 'r') + k('Error', n('Tried - error'), 'r') +
      k('No data', n('Tried - no data found')) + k('No credentials', n('No credentials')) +
      k('Done %', pct === null ? '–' : `${pct}%`, 'g') + k('Action needed', actionNeeded(rows), 'r');
  }

  function ownerCell(r) {
    if (!canAssign) return esc(r.owner_label);
    const cur = r.upload_mode === 'MANUAL' ? 'manual' : r.owner_internal_user_id ? `u:${r.owner_internal_user_id}` : 'none';
    const opts = [['none', UNASSIGNED], ['manual', MANUAL], ...state.users.map(u => [`u:${u.id}`, u.first_name + (u.last_name ? ` ${u.last_name}` : '')])];
    return `<select class="gst-sel gst-assign" data-client="${esc(r.client_id)}">${opts
      .map(([v, l]) => `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select>`;
  }

  function renderHead() {
    $('gst-head').innerHTML = COLUMNS.map(([col, lbl]) => {
      const arrow = state.sort?.col === col ? (state.sort.asc ? ' ↑' : ' ↓') : ' ↕';
      return `<th class="gst-sort" data-col="${col}">${esc(lbl)}${arrow}</th>`;
    }).join('');
  }

  function renderBody(rows) {
    if (!rows.length) {
      $('gst-body').innerHTML = `<tr><td colspan="${COLUMNS.length}" class="no-rows">No records match these filters</td></tr>`;
      return;
    }
    $('gst-body').innerHTML = rows.map(r => {
      const statusCls = STATUS_CLASS[r.upload_status] || 'gst-idle';
      const cell = col => {
        if (col === 'owner_label') return ownerCell(r);
        if (col === 'upload_status') return `<span class="gst-spill ${statusCls}">${esc(r.upload_status)}</span>`;
        if (col === 'gst_number') return `<span class="gst-mono">${esc(r.gst_number)}</span>`;
        if (col === 'client_name') return `<span class="fw6">${esc(rowDisplayValue(r, col))}</span>`;
        return esc(rowDisplayValue(r, col));
      };
      return `<tr>${COLUMNS.map(([col]) => `<td>${cell(col)}</td>`).join('')}</tr>`;
    }).join('');
  }

  function renderSummary() {
    $('gst-sum-asof').textContent = state.asOf ? `Data as of ${fmtIst(state.asOf)} IST` : '';
    if (!state.rows.length) {
      $('gst-sum-body').innerHTML = '<tr><td colspan="11" class="no-rows">No data</td></tr>';
      return;
    }
    const { owners, total } = summarizeByOwner(state.rows);
    const cells = (o, isTotal) => [o.total, o.uploaded, o.locked, o.due, o.password, o.noData, o.error, o.noCreds,
      o.donePct === null ? '–' : `${o.donePct}%`, o.action]
      .map(v => `<td${isTotal ? ' class="fw6"' : ''}>${esc(v)}</td>`).join('');
    $('gst-sum-body').innerHTML = owners.map(o =>
      `<tr class="gst-sum-row" data-owner="${esc(o.owner)}" style="cursor:pointer"><td class="fw6">${esc(o.owner)}</td>${cells(o, false)}</tr>`).join('') +
      `<tr class="gst-total-row"><td class="fw6">Total</td>${cells(total, true)}</tr>`;
  }

  function renderAll() {
    renderSummary();
    renderControls();
    renderHead();
    const rows = filtered();
    renderKpis(rows);
    renderBody(rows);
  }

  // ── Events ──────────────────────────────────────────────────────────────
  $('gst-status-pills').addEventListener('click', e => {
    const s = e.target.closest('[data-status]')?.dataset.status;
    if (!s) return;
    state.statuses.has(s) ? state.statuses.delete(s) : state.statuses.add(s);
    renderAll();
  });
  $('gst-owner').addEventListener('change', e => { state.owner = e.target.value; renderAll(); });
  $('gst-act').addEventListener('change', e => { state.act = e.target.value; renderAll(); });
  $('gst-q').addEventListener('input', e => { state.q = e.target.value; renderAll(); });
  $('gst-refresh').addEventListener('click', load);
  $('gst-sum-refresh').addEventListener('click', load);
  $('gst-sum-body').addEventListener('click', e => {
    const owner = e.target.closest('.gst-sum-row')?.dataset.owner;
    if (!owner) return;
    state.owner = owner;
    state.statuses.clear();
    renderAll();
    window.showSec(SEC_ID, $('gst-nav-tracker'));
  });
  $('gst-sum-export').addEventListener('click', () => {
    const { owners, total } = summarizeByOwner(state.rows);
    const head = ['Responsible Person', 'Total', 'Uploaded today', 'Locked (24h)', 'Due', 'Password issue', 'No data', 'Error', 'No credentials', 'Done %', 'Action needed'];
    const line = o => [o.owner, o.total, o.uploaded, o.locked, o.due, o.password, o.noData, o.error, o.noCreds, o.donePct === null ? '' : `${o.donePct}%`, o.action];
    const ws = XLSX.utils.aoa_to_sheet([head, ...owners.map(line), line(total)]);
    ws['!cols'] = head.map(h => ({ wch: Math.max(12, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard');
    const d = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
    XLSX.writeFile(wb, `GST_Upload_Summary_${d}.xlsx`);
  });
  $('gst-head').addEventListener('click', e => {
    const col = e.target.closest('[data-col]')?.dataset.col;
    if (!col) return;
    state.sort = state.sort?.col === col ? { col, asc: !state.sort.asc } : { col, asc: true };
    renderAll();
  });
  $('gst-export').addEventListener('click', () => {
    const rows = filtered();
    const aoa = [COLUMNS.map(([, l]) => l), ...rows.map(r => COLUMNS.map(([c]) => (c === 'owner_label' ? r.owner_label : rowDisplayValue(r, c))))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = COLUMNS.map(([, l]) => ({ wch: Math.max(14, l.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Today - Detail');
    const d = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
    XLSX.writeFile(wb, `GST_Upload_Tracker_${d}.xlsx`);
  });
  $('gst-body').addEventListener('change', async e => {
    const sel = e.target.closest('.gst-assign');
    if (!sel) return;
    const clientId = Number(sel.dataset.client);
    const v = sel.value;
    const body = v === 'manual' ? { ownerInternalUserId: null, uploadMode: 'MANUAL' }
      : v === 'none' ? { ownerInternalUserId: null, uploadMode: 'AUTO' }
      : { ownerInternalUserId: Number(v.slice(2)), uploadMode: 'AUTO' };
    sel.disabled = true;
    try {
      const res = await fetch(`${baseUrl}/GstUploadOwners/${clientId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${bearerToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const msg = (await res.json().catch(() => ({}))).message;
        throw new Error(msg || `HTTP ${res.status}`);
      }
      await load(); // refresh so every GST of this client shows the new owner
    } catch (err) {
      sel.disabled = false;
      alert(`Could not change owner: ${err.message}`);
      renderAll();
    }
  });

  load();
}
